/*
 * Quick shop grid — design direction test (task 19059).
 *
 * "Quick add" fetches sections/quick-shop-panel.liquid for that product through the
 * Section Rendering API and opens it inline below the grid. One page-level controller
 * handles every trigger by delegation, so cards anywhere on the page work — including
 * the homepage sliders, whose cards are injected from a <template> after load and so
 * can never be wrapped in a component at render time. Unlike quick-add-pills,
 * picking a size does NOT add to the bag: the shopper reviews the panel and presses
 * Add to bag, which is the whole point of the concept.
 *
 * Adding goes through the embedded <product-form>, the same path as the product page,
 * and completion is detected on the cartUpdate / cartError pub-sub events matched by
 * variant id (see assets/quick-add-pills.js for the original of that approach).
 */
if (!customElements.get('quick-shop-grid')) {
  customElements.define(
    'quick-shop-grid',
    class QuickShopGrid extends HTMLElement {
      connectedCallback() {
        // Exactly one controller listens for the whole page. A page can hold several
        // <quick-shop-grid> elements (or none, on the homepage), so the first to connect
        // takes the job and the rest stay inert.
        if (window.quickShopController && window.quickShopController.isConnected) return;
        window.quickShopController = this;

        this.panelSection = this.dataset.panelSection || 'quick-shop-panel';
        this.openTrigger = null;
        this.abortController = null;
        this.onKeydown = this.onKeydown.bind(this);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onClick.bind(this));
      }

      // The panel opens directly under the row the card is in, not at the end of the
      // whole section: on a 60-product collection page those are very different places.
      // Rows are found by comparing sibling top edges, which works for Dawn's product
      // grid (a wrapping flex list) as well as real CSS grids. A horizontally scrolling
      // slider is the exception — its items are all one row, and dropping the panel
      // among them would hide it inside the scroller, so it goes after the slider.
      hostFor(trigger) {
        const owner = trigger.closest('quick-shop-grid');
        const declared = owner && owner.querySelector('[data-quick-shop-host]');
        if (declared) return declared;

        if (this.createdHost) {
          this.createdHost.remove();
          this.createdHost = null;
        }

        // Prefer the outer list item over the inner card wrapper, which sits closer.
        const item =
          trigger.closest('li') ||
          trigger.closest('.card-wrapper, .grid__item, .collection-slider__item');
        const list = item && item.parentElement;
        let anchor = null;
        let spanRow = false;

        const scrollsSideways =
          list && (list.scrollWidth > list.clientWidth + 4 ||
            ['auto', 'scroll'].indexOf(getComputedStyle(list).overflowX) !== -1);

        if (list && !scrollsSideways && list.children.length > 1) {
          const top = item.getBoundingClientRect().top;
          const row = [...list.children].filter(
            (el) => Math.abs(el.getBoundingClientRect().top - top) < 2
          );
          anchor = row[row.length - 1] || item;
          spanRow = true;
        } else {
          anchor =
            trigger.closest('.collection-slider, .collection-products-block, .product-grid') ||
            list ||
            item ||
            trigger;
        }

        const parentTag = anchor.parentElement ? anchor.parentElement.tagName : '';
        const host = document.createElement(parentTag === 'UL' || parentTag === 'OL' ? 'li' : 'div');
        host.className = 'quick-shop__host';
        host.setAttribute('data-quick-shop-host', '');
        host.hidden = true;
        if (spanRow) {
          // Full width in either layout, so it breaks onto its own row.
          host.style.gridColumn = '1 / -1';
          host.style.flexBasis = '100%';
          host.style.width = '100%';
          host.style.maxWidth = '100%';
        }
        anchor.after(host);
        this.createdHost = host;
        return host;
      }

      disconnectedCallback() {
        if (window.quickShopController === this) window.quickShopController = null;
        document.removeEventListener('keydown', this.onKeydown);
        clearTimeout(this.doneTimeout);
        this.unsubscribe();
        if (this.abortController) this.abortController.abort();
      }

      onClick(event) {
        const trigger = event.target.closest('.quick-shop-card__trigger');
        if (trigger) {
          event.preventDefault();
          this.toggle(trigger);
          return;
        }
        if (event.target.closest('.quick-shop__close')) {
          event.preventDefault();
          this.close({ focusTrigger: true });
          return;
        }
        const chip = event.target.closest('.quick-shop__chip');
        if (chip) {
          event.preventDefault();
          this.onChipClick(chip);
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.openTrigger) this.close({ focusTrigger: true });
      }

      toggle(trigger) {
        if (this.openTrigger === trigger) {
          this.close({ focusTrigger: true });
          return;
        }
        this.open(trigger);
      }

      async open(trigger) {
        // A second click while a fetch is in flight should win, not queue behind it.
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.nextGeneration();
        if (this.host && this.host !== this.hostFor(trigger)) this.hideHost(this.host);
        this.host = this.hostFor(trigger);
        this.setTriggerState(trigger, true);
        this.openTrigger = trigger;
        trigger.classList.add('quick-shop-card__trigger--loading');

        // Product links can already carry a query string — search results arrive as
        // /products/x?_pos=1&_sid=… — so the parameter is set properly rather than
        // appended, which would produce a second "?" and return the whole page.
        const url = new URL(trigger.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', this.panelSection);
        try {
          const response = await fetch(url.toString(), { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Quick shop panel failed (${response.status})`);
          const markup = await response.text();
          const panel = new DOMParser().parseFromString(markup, 'text/html').querySelector('.quick-shop__panel');
          if (!panel) throw new Error('Quick shop panel markup not found');

          this.host.innerHTML = '';
          this.host.appendChild(panel);
          this.host.removeAttribute('hidden');
          this.bindPanel(panel);

          // Let layout settle before measuring, so the panel isn't scrolled to
          // while it still has zero height.
          requestAnimationFrame(() => {
            this.host.classList.add('quick-shop__host--open');
            const close = panel.querySelector('.quick-shop__close');
            if (close) close.focus({ preventScroll: true });
            this.scrollPanelIntoView();
          });
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.setTriggerState(trigger, false);
          this.openTrigger = null;
          this.host.setAttribute('hidden', '');
        } finally {
          trigger.classList.remove('quick-shop-card__trigger--loading');
        }
      }

      scrollPanelIntoView() {
        const rect = this.host.getBoundingClientRect();
        const offscreen = rect.bottom > window.innerHeight || rect.top < 0;
        if (!offscreen) return;
        const behavior = QuickShopGrid.prefersReducedMotion() ? 'auto' : 'smooth';
        this.host.scrollIntoView({ behavior, block: 'nearest' });
      }

      close({ focusTrigger = false } = {}) {
        if (!this.openTrigger) return;
        const trigger = this.openTrigger;
        this.unsubscribe();
        this.setTriggerState(trigger, false);
        this.openTrigger = null;
        this.panel = null;

        // Fade out on the same curve it opened on, rather than vanishing. The
        // generation counter means reopening mid-fade doesn't get wiped by the
        // tidy-up from the close that is still in flight.
        const generation = this.nextGeneration();
        this.host.classList.remove('quick-shop__host--open');
        const tidy = () => {
          if (generation !== this.generation) return;
          this.host.setAttribute('hidden', '');
          this.host.innerHTML = '';
        };
        if (QuickShopGrid.prefersReducedMotion()) tidy();
        else setTimeout(tidy, 280);

        if (focusTrigger) trigger.focus({ preventScroll: true });
      }

      hideHost(host) {
        host.classList.remove('quick-shop__host--open');
        host.setAttribute('hidden', '');
        host.innerHTML = '';
      }

      nextGeneration() {
        this.generation = (this.generation || 0) + 1;
        return this.generation;
      }

      static prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      setTriggerState(trigger, open) {
        this.querySelectorAll('.quick-shop-card__trigger').forEach((button) => {
          const isThis = button === trigger;
          button.setAttribute('aria-expanded', isThis && open ? 'true' : 'false');
          button.classList.toggle('quick-shop-card__trigger--active', isThis && open);
        });
      }

      /* ---------- panel ---------- */

      bindPanel(panel) {
        this.panel = panel;
        this.variants = JSON.parse(panel.dataset.variants || '[]');
        this.optionCount = parseInt(panel.dataset.optionCount || '1', 10);
        this.selections = new Array(this.optionCount).fill(null);
        this.chips = panel.querySelectorAll('.quick-shop__chip');
        this.form = panel.querySelector('.quick-shop__form form');
        this.variantInput = panel.querySelector('.product-variant-id');
        this.submit = panel.querySelector('[data-quick-shop-submit]');
        this.submitText = panel.querySelector('[data-quick-shop-submit-text]');
        this.priceEl = panel.querySelector('[data-quick-shop-price]');
        this.errorEl = panel.querySelector('.quick-shop__error');
        this.feedbackEl = panel.querySelector('[data-quick-shop-feedback]');

        // product-form.js ends a successful add by calling cart.renderContents(), which
        // slides the cart drawer over the page. The brief asks for a bag confirmation
        // that keeps customers browsing, so this form gets a no-op cart instead and the
        // panel confirms in place. Everything else about the add is unchanged.
        const form = panel.querySelector('product-form');
        if (form) {
          form.cart = {
            getSectionsToRender: () => [],
            setActiveElement: () => {},
            renderContents: () => {},
            classList: { contains: () => false, remove: () => {}, add: () => {} },
          };
        }

        // An option with a single value (most colourways here) is chosen for the
        // shopper, so only the size is left to pick.
        const groups = new Map();
        this.chips.forEach((chip) => {
          const index = parseInt(chip.dataset.optionIndex, 10);
          if (!groups.has(index)) groups.set(index, []);
          groups.get(index).push(chip);
        });
        groups.forEach((chips, index) => {
          if (chips.length === 1) {
            this.selections[index] = chips[0].dataset.optionValue;
            chips[0].classList.add('quick-shop__chip--selected');
          }
        });

        this.refreshAvailability();
        this.refreshSubmit();
        this.subscribeToCart();
      }

      onChipClick(chip) {
        if (chip.disabled) return;
        const index = parseInt(chip.dataset.optionIndex, 10);
        this.selections[index] = chip.dataset.optionValue;
        this.chips.forEach((other) => {
          if (parseInt(other.dataset.optionIndex, 10) === index) {
            other.classList.toggle('quick-shop__chip--selected', other === chip);
          }
        });
        this.hideError();
        this.refreshAvailability();
        this.refreshSubmit();
      }

      refreshAvailability() {
        this.chips.forEach((chip) => {
          const index = parseInt(chip.dataset.optionIndex, 10);
          const partial = this.selections.slice();
          partial[index] = chip.dataset.optionValue;
          const reachable = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((option, i) => partial[i] === null || option === partial[i])
          );
          chip.classList.toggle('quick-shop__chip--unavailable', !reachable);
          chip.disabled = !reachable;
        });
      }

      currentVariant() {
        if (this.selections.some((selection) => selection === null)) return null;
        return this.variants.find((variant) =>
          variant.options.every((option, i) => option === this.selections[i])
        );
      }

      refreshSubmit() {
        const variant = this.currentVariant();
        // The price line carries the currency code, the button doesn't — same split
        // the panel renders with, so switching size doesn't change the format.
        if (variant && this.priceEl && variant.priceWithCurrency) {
          this.priceEl.textContent = variant.priceWithCurrency;
        }
        if (!variant) {
          this.submit.disabled = true;
          this.submitText.textContent = 'Select a size';
          return;
        }
        if (!variant.available) {
          this.submit.disabled = true;
          this.submitText.textContent = 'Sold out';
          return;
        }
        this.submit.disabled = false;
        this.restingLabel = `Add to bag — ${variant.price}`;
        this.submitText.textContent = this.restingLabel;
      }

      subscribeToCart() {
        this.unsubscribe();
        if (typeof subscribe !== 'function') return;
        this.cartUpdateOff = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.isOwnSubmission(event)) this.finish(true);
        });
        this.cartErrorOff = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (this.isOwnSubmission(event)) {
            this.finish(false);
            this.showError((event && event.message) || 'Could not add to bag.');
          }
        });
      }

      unsubscribe() {
        if (this.cartUpdateOff) this.cartUpdateOff();
        if (this.cartErrorOff) this.cartErrorOff();
        this.cartUpdateOff = null;
        this.cartErrorOff = null;
        clearTimeout(this.submitTimeout);
      }

      isOwnSubmission(event) {
        if (!this.submitting || !event || event.source !== 'product-form') return false;
        return String(event.productVariantId) === this.submittingVariantId;
      }

      finish(success) {
        this.submitting = false;
        this.submittingVariantId = null;
        clearTimeout(this.submitTimeout);
        if (!this.submit) return;
        this.submit.classList.remove('quick-shop__submit--loading');

        if (!success) {
          // Put the button back so it can be tried again.
          this.submit.disabled = false;
          if (this.restingLabel) this.submitText.textContent = this.restingLabel;
          return;
        }

        // Confirm in place and leave the bag shut, which is what the concept asked for:
        // "bag confirmation that keeps customers browsing". Opening the drawer was tried
        // and reverted - it interrupts the grid the shopper is working through, and the
        // header count plus the line below already say the add landed.
        this.submit.classList.add('quick-shop__submit--done');
        this.submitText.textContent = 'Added to bag';
        if (this.feedbackEl) this.feedbackEl.removeAttribute('hidden');
        if (window.dtRefreshCart) window.dtRefreshCart();
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.removeAttribute('hidden');
      }

      hideError() {
        if (this.errorEl) this.errorEl.setAttribute('hidden', '');
      }
    }
  );
}

/*
 * The submit button lives inside a <product-form>, which already handles the fetch to
 * /cart/add.js. We only need to put the chosen variant id on the form before it goes,
 * and to track that a submission is ours so the loading state clears on the right event.
 */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!form.closest('.quick-shop__form')) return;
    // The panel is placed next to whichever grid the card sits in, which is usually
    // outside the controller element, so the controller is reached directly rather
    // than by walking up from the form.
    const grid = window.quickShopController;
    if (!grid || !grid.panel || !grid.panel.contains(form)) return;

    const variant = grid.currentVariant();
    if (!variant || !variant.available) {
      event.preventDefault();
      grid.showError('Choose a size first.');
      return;
    }

    grid.variantInput.value = variant.id;
    grid.variantInput.disabled = false;
    grid.submitting = true;
    grid.submittingVariantId = String(variant.id);
    grid.submit.classList.add('quick-shop__submit--loading');
    grid.submit.disabled = true;
    grid.submitText.textContent = 'Adding to bag…';

    // If neither cartUpdate nor cartError arrives, don't leave the button spinning.
    clearTimeout(grid.submitTimeout);
    grid.submitTimeout = setTimeout(() => {
      if (grid.submitting) {
        grid.finish(false);
        grid.showError('That took too long. Please try again.');
      }
    }, 10000);
  },
  true
);

// Cards can appear on pages that render no <quick-shop-grid> of their own — the homepage
// sliders being the case that matters — so make sure a controller exists to serve them.
function ensureQuickShopController() {
  // No check for triggers here: the slider cards live in a <template> and are injected
  // later, so at this point there is nothing to find.
  if (document.querySelector('quick-shop-grid')) return;
  const controller = document.createElement('quick-shop-grid');
  controller.hidden = true;
  document.body.appendChild(controller);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureQuickShopController);
} else {
  ensureQuickShopController();
}
